import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, ArrowUpRight, Trophy, Smartphone, Gift, Sparkles } from "lucide-react";
import { api, apiErr } from "../lib/api";
import { WeeklyCountdown, useLiveProjectCount } from "../components/Countdown";
import EditorialMarquee from "../components/EditorialMarquee";
import Reveal, { Label } from "../components/Reveal";
import { ConvergeMark } from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { useLang } from "../context/LanguageContext";
import Seo from "../components/Seo";

function HeroReveal({ title, accent }) {
  return (
    <h1 className="font-display font-900 leading-[0.95] tracking-tighter text-white">
      <span className="block overflow-hidden">
        <motion.span
          className="block text-4xl sm:text-6xl lg:text-7xl"
          initial={{ y: "110%" }} animate={{ y: "0%" }}
          transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        >
          {title}
        </motion.span>
      </span>
      <span className="block overflow-hidden">
        <motion.span
          className="mt-2 block text-3xl sm:text-5xl lg:text-6xl text-cyan text-glow"
          initial={{ y: "110%" }} animate={{ y: "0%" }}
          transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          {accent}
        </motion.span>
      </span>
    </h1>
  );
}

export default function Home() {
  const [weekly, setWeekly] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [contact, setContact] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const nav = useNavigate();
  const { user } = useAuth();
  const { settings } = useSettings();
  const { t } = useLang();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yVisual = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    api.get("/public/weekly").then((r) => setWeekly(r.data)).catch(() => {});
    api.get("/public/gallery").then((r) => setGallery(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((x) => x + 1), 30000);
    return () => clearInterval(id);
  }, []);
  // Idées proposées : compteur synchronisé avec le blog (base réglable dans l'admin).
  const live = useLiveProjectCount();
  const liveIdeas = live ? live.count : "…";
  // Projets présélectionnés : pourcentage des idées proposées (réglable dans l'admin).
  const livePresel = live ? Math.round(live.count * live.pct / 100) : 0;

  const goDeposer = () => nav(user ? "/app/deposer" : "/inscription");

  const submitContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/public/contact", contact);
      toast.success(t("ct_ok"));
      setContact({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(apiErr(err));
    } finally { setSending(false); }
  };

  const pillars = [
    { icon: Trophy, t: t("pill1_t"), s: t("pill1_s") },
    { icon: Smartphone, t: t("pill2_t"), s: t("pill2_s") },
    { icon: Gift, t: t("pill3_t"), s: t("pill3_s") },
  ];

  const steps = [
    { n: "01", t: t("mf_s1t"), d: t("mf_s1d") },
    { n: "02", t: t("mf_s2t"), d: t("mf_s2d") },
    { n: "03", t: t("mf_s3t"), d: t("mf_s3d") },
    { n: "04", t: t("mf_s4t"), d: t("mf_s4d") },
    { n: "05", t: t("mf_s5t"), d: t("mf_s5d") },
  ];

  return (
    <div>
      <Seo path="/" />
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden pt-32 pb-20 sm:pt-40">
        <div className="pointer-events-none absolute -top-40 right-0 h-[600px] w-[600px] rounded-full bg-electric/20 blur-[140px]" />
        <div className="pointer-events-none absolute top-40 -left-40 h-[400px] w-[400px] rounded-full bg-cyan/10 blur-[120px]" />
        <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-5 sm:px-8 lg:grid-cols-[1.55fr_1fr]">
          <motion.div style={{ opacity }}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
              <Label>{t("hero_badge")}</Label>
            </motion.div>
            <div className="mt-6"><HeroReveal title={t("hero_title")} accent={t("hero_accent")} /></div>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }}
              className="mt-8 max-w-xl text-base leading-relaxed text-ink-secondary sm:text-lg"
            >
              {t("hero_sub")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.7 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-cta-deposer"
                onClick={goDeposer}
                className="group inline-flex items-center gap-2 bg-cyan px-7 py-4 text-sm font-600 uppercase tracking-wide text-void transition-all hover:-translate-y-0.5 hover:glow-cyan"
              >
                {t("cta_deposit")}
                <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
              </button>
              <Link
                to="/comment-ca-marche"
                data-testid="hero-how-link"
                className={`group inline-flex items-center gap-1.5 text-sm text-ink-secondary transition-colors hover:text-white ${settings.nav_how ? "" : "hidden"}`}
              >
                {t("hero_how")} <ArrowUpRight size={16} className="text-cyan" />
              </Link>
            </motion.div>
          </motion.div>

          <motion.div style={{ y: yVisual }} className="relative hidden lg:block">
            <div className="relative mx-auto aspect-square max-w-[420px]">
              <div className="absolute inset-0 rounded-full border border-white/10 animate-spin-slow" />
              <div className="absolute inset-8 rounded-full border border-cyan/20" />
              <ConvergeMark className="absolute inset-0 h-full w-full p-6" />
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
                <Sparkles className="mx-auto text-cyan" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WEEKLY CHALLENGE — tracing beam card */}
      <section className="relative mx-auto -mt-2 max-w-[1400px] px-5 sm:px-8">
        <Reveal>
          <div className="relative overflow-hidden border border-white/10 bg-surface p-8 sm:p-12" data-testid="weekly-card">
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan to-transparent" />
            <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
              <div>
                <Label className="text-[11px] font-700">{t("this_week")}</Label>
                <div className="mt-6 space-y-6">
                  {weekly && [
                    { v: liveIdeas, l: t("ideas_proposed") },
                    { v: livePresel, l: t("preselected_projects") },
                    { v: weekly.winners_count, l: Number(weekly.winners_count) > 1 ? t("many_selected") : t("one_selected") },
                  ].map((s, i) => (
                    <div key={i} className="flex items-baseline gap-4 border-b border-white/5 pb-4">
                      <span className="font-display font-700 tabular-nums text-4xl text-white sm:text-5xl">{s.v}</span>
                      <span className="text-sm text-ink-secondary">{s.l}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-col justify-center">
                <div className="mb-4 text-[11px] uppercase tracking-[0.25em] text-ink-tertiary">{t("next_selection")}</div>
                <WeeklyCountdown target={weekly?.countdown_mode === "fixed" ? weekly.next_selection : null} />
                <button
                  data-testid="weekly-cta"
                  onClick={goDeposer}
                  className="group mt-8 inline-flex w-fit items-center gap-2.5 rounded-full border border-cyan/40 px-9 py-4 text-base font-700 text-cyan transition-all hover:-translate-y-0.5 hover:bg-cyan hover:text-void hover:glow-cyan"
                >
                  {t("participate")} <ArrowRight size={19} className="transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </div>
            <p className="mt-10 border-t border-white/10 pt-6 text-center text-sm font-600 leading-relaxed text-cyan" data-testid="weekly-urgency">
              {t("weekly_urgency")}
            </p>
          </div>
        </Reveal>
      </section>

      {/* PILLARS */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8">
        <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-3">
          {pillars.map((p, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="group h-full bg-void p-8 transition-colors hover:bg-surface" data-testid={`pillar-${i}`}>
                <p.icon className="text-cyan" strokeWidth={1.4} />
                <div className="mt-8 font-display text-2xl font-700 text-white">{p.t}</div>
                <div className="mt-1 text-sm text-ink-secondary">{p.s}</div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-12 max-w-3xl font-display text-2xl leading-snug text-white sm:text-3xl">
            {t("conclusion")}
          </p>
        </Reveal>
      </section>

      {/* MANIFESTO */}
      <section className="border-y border-white/10 bg-surface py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal><Label>{t("mf_label")}</Label></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 max-w-2xl font-display text-3xl font-700 leading-tight text-white sm:text-4xl">
              {t("mf_title")}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {steps.map((s, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className={`group relative h-full overflow-hidden bg-surface p-8 transition-colors hover:bg-elevated ${i === 4 ? "lg:col-span-1" : ""}`} data-testid={`step-${s.n}`}>
                  <span className="relative inline-block bg-gradient-to-br from-cyan via-cyan to-white bg-clip-text font-display text-7xl font-900 text-transparent drop-shadow-[0_0_24px_rgba(0,240,255,0.45)] transition-transform duration-300 group-hover:scale-110">
                    {s.n}
                  </span>
                  <div className="mt-6 font-display text-xl font-600 text-white">{s.t}</div>
                  <p className="mt-2 text-sm leading-relaxed text-ink-secondary">{s.d}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="flex h-full flex-col justify-between bg-electric/10 p-8">
                <span className="font-display text-lg font-700 text-white">{t("mf_card")}</span>
                <button onClick={goDeposer} data-testid="manifesto-cta" className="group mt-6 inline-flex w-fit items-center gap-2 text-sm font-600 text-cyan">
                  {t("mf_card_cta")} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      {weekly?.categories && (
        <section className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.3fr]">
            <Reveal>
              <Label>{t("cat_label")}</Label>
              <h2 className="mt-5 font-display text-3xl font-700 leading-tight text-white">
                {t("cat_title")}
              </h2>
              <p className="mt-5 text-sm leading-relaxed text-ink-secondary">
                {t("cat_desc")}
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="space-y-5">
                {weekly.categories.map((c, i) => (
                  <div key={i} data-testid={`category-${i}`}>
                    <div className="mb-2 flex items-center justify-between text-sm">
                      <span className="text-white">{c.name}</span>
                      <span className="tabular-nums text-cyan">{c.percent}%</span>
                    </div>
                    <div className="h-1.5 w-full bg-elevated">
                      <motion.div
                        className="h-full bg-cyan"
                        initial={{ width: 0 }} whileInView={{ width: `${c.percent}%` }}
                        viewport={{ once: true }} transition={{ duration: 1, delay: i * 0.08, ease: "easeOut" }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* MARQUEE */}
      <EditorialMarquee />

      {/* GALLERY TEASER */}
      {settings.home_gallery_teaser && (
      <section className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <Label>{t("gal_label")}</Label>
            <h2 className="mt-5 font-display text-3xl font-700 leading-tight text-white sm:text-4xl">
              {t("gal_title1")}<br /><span className="text-cyan">{t("gal_title2")}</span>
            </h2>
          </Reveal>
          <Reveal>
            <Link to="/applications" data-testid="gallery-all-link" className="group inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm text-white transition-all hover:-translate-y-0.5 hover:border-cyan">
              {t("gal_view")} <ArrowUpRight size={16} className="text-cyan" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {gallery.map((g, i) => (
            <Reveal key={g.id} delay={i * 0.08}>
              <Link to="/applications" className="group block" data-testid={`gallery-teaser-${i}`}>
                <div className="relative aspect-[9/12] overflow-hidden border border-white/10 bg-surface">
                  <div className="absolute inset-0 bg-gradient-to-t from-void via-transparent to-transparent opacity-80" />
                  <img src={g.image} alt={g.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <div className="text-[11px] uppercase tracking-widest text-cyan">{g.category}</div>
                    <div className="mt-1 font-display text-xl font-700 text-white">{g.name}</div>
                    <div className="text-xs text-ink-secondary">par {g.creator} · {g.country}</div>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
      )}

      {/* CONTACT */}
      <section id="contact" className="border-t border-white/10 bg-surface py-24">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-5 sm:px-8 lg:grid-cols-[1fr_1fr]">
          <Reveal>
            <Label>{t("ct_label")}</Label>
            <h2 className="mt-5 font-display text-3xl font-700 leading-tight text-white sm:text-4xl">
              {t("ct_title")}
            </h2>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-ink-secondary">
              {t("ct_desc")}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <form onSubmit={submitContact} className="space-y-4" data-testid="contact-form">
              <input
                required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })}
                placeholder={t("ct_name")} data-testid="contact-name"
                className="w-full rounded-xl border border-white/10 bg-void px-4 py-3.5 text-sm text-white placeholder:text-ink-tertiary outline-none transition-colors focus:border-cyan focus:ring-1 focus:ring-cyan"
              />
              <input
                required type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })}
                placeholder={t("ct_email")} data-testid="contact-email"
                className="w-full rounded-xl border border-white/10 bg-void px-4 py-3.5 text-sm text-white placeholder:text-ink-tertiary outline-none transition-colors focus:border-cyan focus:ring-1 focus:ring-cyan"
              />
              <textarea
                required rows={5} value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })}
                placeholder={t("ct_msg")} data-testid="contact-message"
                className="w-full resize-none rounded-xl border border-white/10 bg-void px-4 py-3.5 text-sm text-white placeholder:text-ink-tertiary outline-none transition-colors focus:border-cyan focus:ring-1 focus:ring-cyan"
              />
              <button
                type="submit" disabled={sending} data-testid="contact-submit"
                className="group inline-flex items-center gap-2 bg-cyan px-7 py-4 text-sm font-600 uppercase tracking-wide text-void transition-all hover:-translate-y-0.5 hover:glow-cyan disabled:opacity-60"
              >
                {sending ? t("ct_sending") : t("ct_send")}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
